import { css } from '@emotion/react';
import { Link, LinkProps } from './link';
import { ReactElement, Children, cloneElement } from 'react';
import { Button, ButtonProps } from './button';
import { breakpoints } from '../foundations';

type TableChildren =
  | typeof TableCell
  | typeof FeatureDivider
  | typeof TitleRow
  | typeof TableRow
  | typeof PricingCard;

interface DividerProps {
  children: string;
  colCount: number;
  isHeading?: boolean;
  isShaded?: boolean;
}

interface TableRowProps {
  columns?: string[];
  children: ReactElement<typeof TableCell> | ReactElement<typeof TableCell>[];
}

export interface TableProps {
  children: ReactElement<TableChildren> | ReactElement<TableChildren>[];
  columns: string[];
}

interface TitleRowProps {
  columns?: string[];
  isShaded?: boolean;
  isSmall?: boolean;
  centerText?: boolean;
}

interface PricingCardProps {
  title: string;
  price: number;
  helpText: string;
  buttonProps: ButtonProps;
}

//TODO: replace iconProps with a real type when the icon component is done
interface TableCellProps {
  column?: string;
  isFirst?: boolean;
  centerText?: boolean;
  contentProps: CellContentProps;
  isShaded?: boolean;
  isInlineDivider?: boolean;
}

const tableStyles = css`
  h3,
  h4 {
    margin: 0;
    font-size: inherit;
    font-weight: inherit;
    padding: 20px;
  }
  p {
    margin: 4px;
  }
  td,
  tr {
    padding: 0;
  }

  @media (min-width: ${breakpoints['md']}px) {
    table-layout: fixed;
    th,
    td {
      display: table-cell;
      width: auto;
    }

    tr {
      display: table-row;
    }
  }

  @media (max-width: ${breakpoints['md'] - 1}px) {
    display: flex;
    flex-direction: column;
  }

  @media (max-width: ${breakpoints['md']}px) {
    tr {
      display: flex;
      flex-direction: column;
    }
  }

  border-collapse: collapse;
  width: 100%;
`;

type boollike = boolean | undefined;

const titleRowStyles = (
  shaded: boollike,
  centerText: boollike,
  isSmall: boollike
) => {
  return css`
    background: ${shaded ? '#F4F5F7' : '#FFFFFF'};
    box-shadow: -1px 0px 0px 0px #dfe1e6 inset, 1px 0px 0px 0px #dfe1e6 inset,
      0px -1px 0px 0px #dfe1e6 inset, 0px 1px 0px 0px #dfe1e6 inset;
    color: #091e42;
    font-size: ${isSmall ? `1.25rem` : `1.5rem`};
    font-style: normal;
    font-weight: 600;
    line-height: ${isSmall ? '1.5rem' : '2rem'};
    letter-spacing: 0.3px;
    text-align: ${centerText ? 'center' : 'left'};
  `;
};

const dividerStyles = (shaded: boollike, isHeading: boollike) => css`
  background: ${shaded ? '#F4F5F7' : '#FFFFFF'};
  color: #091e42;
  font-size: ${isHeading ? '1.25rem' : '1.125rem'};
  font-style: normal;
  font-weight: 600;
  line-height: ${isHeading ? '1.5rem' : '1.625rem'};
  letter-spacing: 0.3px;
  text-align: ${isHeading ? 'center' : 'left'};
`;

const tableCellStyles = (
  shaded: boollike,
  centerText: boollike,
  isInlineDivider: boollike
) => {
  return css`
    background: ${shaded || isInlineDivider ? '#F4F5F7' : '#FFFFFF'};
    box-shadow: -1px 0px 0px 0px #dfe1e6 inset, 1px 0px 0px 0px #dfe1e6 inset,
      0px -1px 0px 0px #dfe1e6 inset, 0px 1px 0px 0px #dfe1e6 inset;
    color: #091e42;
    font-size: 1.125rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.625rem;
    letter-spacing: 0.3px;
    text-align: ${centerText ? 'center' : 'left'};
  `;
};

const priceTitleStyles = css`
  background: #ffffff;
  border: 1px solid #dfe1e6;
  color: #091e42;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 2rem;
  letter-spacing: 0.3px;
  text-align: center;
`;

const priceHeadingStyles = css`
  color: #091e42;
  font-size: 3.375rem;
  font-style: normal;
  font-weight: 700;
  line-height: 3.75rem;
  text-align: center;
`;

const priceHelpStyles = css`
  color: #091e42;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
`;

export const Table = ({ children, columns }: TableProps) => {
  const injectProps: Partial<TableRowProps> = { columns };
  const renderChildren = () => {
    return Children.map(children, (child) => cloneElement(child, injectProps));
  };
  return <table css={tableStyles}>{renderChildren()}</table>;
};

export const TableHeader = ({ children, colCount }: DividerProps) => (
  <tr>
    <td colSpan={colCount} css={titleRowStyles(true, true, false)}>
      <h3>{children}</h3>
    </td>
  </tr>
);

export const FeatureDivider = ({
  children,
  colCount,
  isHeading,
  isShaded
}: DividerProps) => (
  <tr>
    <td
      colSpan={colCount}
      css={[
        css`
          background: #fafbfc;
          border: 1px solid #dfe1e6;
          ${isHeading ? 'text-align: center;' : 'text-align: left;'}
          @media (max-width: ${breakpoints['md'] - 1}px) {
            text-align: left;
          }
        `,
        dividerStyles(isShaded, isHeading)
      ]}
    >
      {isHeading ? <h4>{children}</h4> : <p>{children}</p>}
    </td>
  </tr>
);

export const TitleRow = ({ columns, centerText, isSmall }: TitleRowProps) => {
  const mapColumns = columns || [];
  return (
    <thead
      css={css`
        @media (max-width: ${breakpoints['md'] - 1}px) {
          display: none;
        }
      `}
    >
      <tr>
        {mapColumns.map((text: string, idx: number) => (
          <th css={[titleRowStyles(idx != 0, centerText, isSmall)]} key={text}>
            {isSmall ? <h4>{text}</h4> : <h3>{text}</h3>}
          </th>
        ))}
      </tr>
    </thead>
  );
};

export const TableRow = ({ children, columns }: TableRowProps) => {
  const mapColumns = columns || [];
  const renderChildren = (
    children: ReactElement<typeof TableCell> | ReactElement<typeof TableCell>[]
  ) => {
    return Children.map(children, (child, idx) => {
      const injectProps: Partial<TableCellProps> = {
        column: mapColumns[idx],
        isFirst: idx === 0
      };
      return cloneElement(child, injectProps);
    });
  };
  return (
    <tbody>
      <tr>{renderChildren(children)}</tr>
      <br
        css={css`
          @media (min-width: ${breakpoints['md']}px) {
            display: none;
          }
        `}
      />
    </tbody>
  );
};

//this is temporary; to be replace when the icon component is pulled in
type IconProps = never;
interface CellTextProps {
  text: string;
  isUnderlined: boollike;
}

interface CellLinkProps {
  linkProps: LinkProps;
}

interface CellIconProps {
  iconProps: IconProps;
}
type CellContentProps = CellTextProps | CellIconProps | CellLinkProps;
const renderCellContent = (props: CellContentProps) => {
  if (!props) {
    return null;
  }
  if ('text' in props) {
    const { text, isUnderlined } = props;
    return (
      <p
        css={
          isUnderlined &&
          css`
            text-decoration-color: #a5adba;
            text-decoration-line: underline;
            text-decoration-style: dashed;
            text-underline-position: under;
          `
        }
      >
        {text}
      </p>
    );
  } else if ('linkProps' in props) {
    return <Link {...props.linkProps} />;
  } else if ('iconProps' in props) {
    return null;
  }
  return null;
};

export const TableCell = ({
  contentProps,
  column,
  isShaded,
  isInlineDivider,
  centerText,
  isFirst
}: TableCellProps) => {
  return (
    <td css={tableCellStyles(isShaded, centerText, isInlineDivider)}>
      <div
        css={[
          css`
            display: flex;
            flex-direction: row;
            justify-content: center;
            @media (max-width: ${breakpoints['md']}px) {
              justify-content: space-between;
              padding: 16px;
            }
          `,
          isFirst &&
            css`
              display: inline;
              text-align: left;
              @media (max-width: ${breakpoints['md'] - 1}px) {
                border: 1px solid #dfe1e6;
                background: #fafbfc;
                display: block;
                font-size: 1rem;
                font-style: normal;
                font-weight: 600;
                line-height: 1.25rem; /* 125% */
                margin: auto;
                letter-spacing: 0.3px;
              }
            `
        ]}
      >
        {!isFirst && (
          <p
            css={[
              css`
                @media (min-width: ${breakpoints['md']}px) {
                  display: none;
                }
              `
            ]}
          >
            {column}
          </p>
        )}
        {renderCellContent(contentProps)}
      </div>
    </td>
  );
};

const pricingContainerStyles = css`
  border-collapse: collapse;
  border: 1px solid #dfe1e6;
`;

const pricingLayoutStyles = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const renderPricingCard = (props: PricingProps) => {
  if (!props) {
    return null;
  } else {
    const { title, price, helpText, buttonProps } = props;
    return (
      <div css={pricingLayoutStyles}>
        <div
          css={[
            priceTitleStyles,
            css`
              width: 100%;
            `
          ]}
        >
          <h3>{title}</h3>
        </div>
        <div css={priceHeadingStyles}>
          <h3>{`$${price}`}</h3>
        </div>
        <div css={priceHelpStyles}>
          <p>{helpText}</p>
        </div>
        <Button {...buttonProps} />
      </div>
    );
  }
};

type PricingProps = PricingCardProps | undefined;
export const PricingCard = (props: PricingProps) => {
  return <td css={pricingContainerStyles}>{renderPricingCard(props)}</td>;
};
